import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import type { DescriptionsProps } from "antd";
import { Alert, Avatar, Button, Descriptions, Typography } from "antd";
import {
    LinkOutlined,
    ShoppingOutlined,
    CopyOutlined,
    ApiOutlined,
    IdcardOutlined,
    AppstoreOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import type { MerchantDetailType } from "@src/types";
import { useMerchantUpdate } from "@src/queries";
import MarketingCabinetRegisterDrawer from "./drawers/MarketingCabinetRegisterDrawer";

const { Text } = Typography;

export type MerchantCardProps = {
    data: MerchantDetailType;
};

export default function MerchantDescription({ data }: MerchantCardProps) {
    const {
        id,
        store_name,
        merchant_uid,
        logo_url,
        merchant_url,
        merchant_all_products_url,
        api_token,
        marketing_merchant,
    } = data;

    const [draftToken, setDraftToken] = useState<string>(api_token ?? "");
    const latestRef = useRef<string>(draftToken);
    const { mutate, isPending } = useMerchantUpdate(id);
    const [registerDrawerOpen, setRegisterDrawerOpen] = useState(false);

    const openRegisterDrawer = useCallback(() => setRegisterDrawerOpen(true), []);
    const closeRegisterDrawer = useCallback(() => setRegisterDrawerOpen(false), []);

    const handleChange = useCallback((next: string) => {
        latestRef.current = next;
        setDraftToken(next);
    }, []);

    useEffect(() => {
        latestRef.current = api_token ?? "";
        setDraftToken(api_token ?? "");
    }, [api_token]);

    const handleEnd = useCallback(() => {
        const value = (latestRef.current || "").trim();
        if ((api_token ?? "") === value) return;
        mutate(
            { api_token: value },
            {
                onError: () => {
                    // откатываемся к серверному значению
                    latestRef.current = api_token ?? "";
                    setDraftToken(api_token ?? "");
                },
            },
        );
    }, [api_token, mutate]);

    const items: DescriptionsProps["items"] = useMemo(
        () => [
            {
                key: "store",
                label: (
                    <span>
                        {logo_url ? (
                            <Avatar size={28} src={logo_url} alt={store_name} />
                        ) : (
                            <AppstoreOutlined />
                        )}{" "}
                        Магазин
                    </span>
                ),
                children: (
                    <div className="flex items-center gap-2">
                        <Text strong>{store_name || "—"}</Text>
                    </div>
                ),
            },
            {
                key: "uid",
                label: (
                    <span>
                        <IdcardOutlined /> UID
                    </span>
                ),
                children: (
                    <Text copyable={{ text: merchant_uid, icon: <CopyOutlined /> }}>
                        {merchant_uid || "—"}
                    </Text>
                ),
            },
            {
                key: "site",
                label: (
                    <span>
                        <LinkOutlined /> Сайт магазина
                    </span>
                ),
                children: merchant_url ? (
                    <a href={merchant_url} target="_blank" rel="noreferrer">
                        Открыть
                    </a>
                ) : (
                    "—"
                ),
            },
            {
                key: "all_products",
                label: (
                    <span>
                        <ShoppingOutlined /> Все товары
                    </span>
                ),
                children: merchant_all_products_url ? (
                    <a href={merchant_all_products_url} target="_blank" rel="noreferrer">
                        Перейти
                    </a>
                ) : (
                    "—"
                ),
            },
            {
                key: "api",
                label: <span>{isPending ? <LoadingOutlined /> : <ApiOutlined />} API токен</span>,
                children: (
                    <Text
                        key={id}
                        code={!!draftToken}
                        mark={!draftToken}
                        copyable={!!draftToken}
                        ellipsis={!!draftToken}
                        editable={
                            isPending
                                ? false
                                : {
                                      text: draftToken,
                                      onChange: handleChange,
                                      onEnd: handleEnd,
                                      triggerType: ["icon", "text"],
                                      tooltip: "Изменить API токен",
                                  }
                        }
                    >
                        {draftToken || "У вас нет токена, пожалуйста добавьте!"}
                    </Text>
                ),
            },
        ],
        [
            logo_url,
            store_name,
            merchant_uid,
            merchant_url,
            merchant_all_products_url,
            isPending,
            id,
            draftToken,
            handleChange,
            handleEnd,
        ],
    );

    return (
        <div className="w-full">
            {!marketing_merchant ? (
                <div className="mb-4">
                    <Alert
                        type="warning"
                        showIcon
                        message="Маркетинговый кабинет не подключен"
                        description="Зарегистрируйте маркетинговый кабинет, чтобы подключить мерчанта к рекламным инструментам."
                        action={
                            <Button type="primary" onClick={openRegisterDrawer}>
                                Зарегистрировать кабинет
                            </Button>
                        }
                    />
                </div>
            ) : null}
            <Descriptions
                column={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 }}
                styles={{ label: { width: 150 } }}
                items={items}
            />
            <MarketingCabinetRegisterDrawer
                open={registerDrawerOpen}
                onClose={closeRegisterDrawer}
            />
        </div>
    );
}
